import { LateralModal } from "@/pages";
import {
  BlockOutlined,
  CheckOutlined,
  ContactsOutlined,
  LockOutlined,
  ShopOutlined,
  UsergroupAddOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import {
  Typography,
  Flex,
  Button,
  Modal,
  Select,
  message,
  Spin,
  Card,
  Divider,
} from "antd";
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PermissionsSelect } from "../permissions-select";
import { PermissionSelectRow } from "./permissions-selectRow";
import { StoreList } from "../drag-list";
import { SellerIcon } from "../icon";
import {
  API_URL_PERMISSIONS,
  API_URL_PERMISSIONS_PARTICIPATION,
  API_URL_USERS,
} from "@/services/XCONS_ENV";
import { axiosInstance } from "@/utilities";
import { NormalModal } from "../modals/NormalModal";
import {
  filterOutOperators,
  useOperatorContacts,
} from "@/hooks/useOperatorContacts";

const { Text } = Typography;
const { Option } = Select;

const PERMISSION_CONFIG = [
  { code: "order", label: "Order", badge: false, strong: true },
  {
    code: "order_payment",
    label: "Transacciones de pago",
    badge: true,
    strong: false,
  },
  { code: "order_shipment", label: "Entregas", badge: true, strong: false },
];

type Permission = {
  id: number;
  userId: number;
  contactId: number;
  permissionCode: string;
  entityId: number;
  participationType: string;
  participationIdentifier: string;
  value: string;
  position: number;
};

type UserPermissions = {
  contactId: number;
  userId: number;
  participationType: "customer" | "store" | "seller";
  participationIdentifier: string;
  firstname?: string;
  lastname?: string;
  permissions: {
    order?: Permission;
    order_payment?: Permission;
    order_shipment?: Permission;
    [key: string]: Permission | undefined;
  };
};

export const PermissionsLateral = ({
  recordOrderId,
  contactRecord,
  store,
  initialParticipations,
  onChange,
}: {
  recordOrderId: string;
  contactRecord: any;
  store?: { code: string; name: string; link: string };
  initialParticipations?: any[];
  /**
   * Fires after every successful participation mutation (add, update, position
   * change, remove). The parent (e.g. OrderDetails) uses it to refetch the
   * order so the header / customer card reflect the new owner.
   */
  onChange?: () => void;
}) => {
  const [isLoadingChange, setIsLoadingChange] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [participants, setParticipants] = useState<UserPermissions[]>([]);
  const [draftChanges, setDraftChanges] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const operatorContactIds = useOperatorContacts();

  const { t } = useTranslation();

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // Group by the unique tuple of `permission_participation`: same contactId
  // may have multiple rows (one as store, another as seller — or two seller
  // rows with distinct sellerCodes). Without this the side panel collapsed
  // them into a single entry and the Store section appeared empty for any
  // operator that ALSO had a seller row.
  const buildGroupKey = (
    contactId: number,
    participationType: string,
    participationIdentifier: string,
  ) => `${contactId}::${participationType}::${participationIdentifier}`;

  const groupPermissions = (
    allPermissions: Permission[],
  ): UserPermissions[] => {
    if (allPermissions.length === 0) {
      return [];
    }

    const visiblePermissions = filterOutOperators(
      allPermissions as any[],
      operatorContactIds,
    );

    const grouped: Record<string, UserPermissions> = {};

    visiblePermissions.forEach((perm: any) => {
      if (!perm || !perm.contact) return;
      const cId = perm.contactId;
      const user = perm.contact.user;
      const rawType = (perm.participationType ?? "").trim().toLowerCase();
      const normalizedType: "customer" | "store" | "seller" =
        rawType === "" || rawType === "customer"
          ? "customer"
          : (rawType as "store" | "seller");
      const identifier = perm.participationIdentifier || "";
      const key = buildGroupKey(cId, normalizedType, identifier);

      if (!grouped[key]) {
        grouped[key] = {
          contactId: cId,
          userId: user?.userId || perm.userId,
          participationType: normalizedType,
          participationIdentifier: identifier,
          firstname: user?.firstname || "Unknown",
          lastname: user?.lastname || "",
          permissions: {},
        };
      }

      grouped[key].permissions[perm.permissionCode] = perm;
    });

    // Sort by position on fetch
    return Object.values(grouped).sort((a, b) => {
      const posA = a.permissions.order?.position ?? 0;
      const posB = b.permissions.order?.position ?? 0;
      return posA - posB;
    });
  };

  const fetchAndGroupPermissions = async (
    recordOrderId: string,
  ): Promise<UserPermissions[]> => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `${API_URL_PERMISSIONS_PARTICIPATION}/entity/${recordOrderId}?codes=order,order_payment,order_shipment`,
      );
      const grouped = res.data || {};
      const allPermissions: Permission[] = [
        ...(grouped.order || []),
        ...(grouped.order_payment || []),
        ...(grouped.order_shipment || []),
      ];
      return groupPermissions(allPermissions);
    } catch (error) {
      console.error("Error al obtener permisos:", error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialParticipations?.length) {
      setParticipants(groupPermissions(initialParticipations));
    } else if (recordOrderId) {
      fetchAndGroupPermissions(recordOrderId).then(setParticipants);
    }
    // Re-run when operatorContactIds resolves (initial render may have an
    // empty set while the request is in flight).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordOrderId, operatorContactIds]);

  const handlePermissionChange = async (
    participant: UserPermissions,
    permissionCode: string,
    newValue: string,
  ) => {
    if (newValue === "remove" && participant?.participationType === "customer") {
      const perm = participant.permissions[permissionCode];
      if (!perm) return;
      Modal.confirm({
        title: "¿Estas seguro que deseas eliminar esta relación?",
        okText: "Eliminar",
        cancelText: "Cancelar",
        okType: "danger",
        onOk: async () => {
          try {
            await axiosInstance.delete(
              `${API_URL_PERMISSIONS_PARTICIPATION}/${perm.id}`,
            );
            message.success("Participante eliminado");
            const updated = await fetchAndGroupPermissions(recordOrderId);
            setParticipants(updated);
            onChange?.();
          } catch (error: any) {
            console.error("Error al eliminar participante", error);
            if (error?.message === "You do not have permission") {
              message.error("No tienes permiso para eliminar este participante.");
            } else {
              message.error("Error al intentar eliminar el participante.");
            }
          }
        },
      });
      return;
    }

    // Encode the full participation tuple in the draft key so two rows with
    // the same contactId (e.g. operator that is both store + seller) keep
    // independent drafts.
    const groupKey = buildGroupKey(
      participant.contactId,
      participant.participationType,
      participant.participationIdentifier,
    );
    const key = `${groupKey}||${permissionCode}`;
    const newChanges = { ...draftChanges, [key]: newValue };
    setDraftChanges(newChanges);
    handleSaveChanges(newChanges);
  };

  const handleUpdatePosition = async (newList: any[], type: string) => {
    setIsLoadingChange(true);
    try {
      const updates: any[] = [];
      // Backend EntityType enum maps Customer to ''. The drag-list categorizes
      // customers under the "customer" key, so we translate before POSTing.
      const backendType = type === "customer" ? "" : type;

      newList.forEach((item) => {
        const participant = item.raw.participant;
        const newPos = item.position;

        // Ensure all 3 permissions are updated to the same position
        PERMISSION_CONFIG.forEach((conf) => {
          const perm = participant.permissions[conf.code];
          if (perm && perm.position !== newPos) {
            updates.push({
              contactId: participant.contactId,
              permissionCode: conf.code,
              entityId: Number(recordOrderId),
              participationType: backendType,
              participationIdentifier: participant.participationIdentifier,
              value: perm.value,
              position: newPos,
            });
          }
        });
      });

      if (updates.length > 0) {
        await Promise.all(
          updates.map((payload) =>
            axiosInstance.post(API_URL_PERMISSIONS_PARTICIPATION, payload),
          ),
        );
        message.success("Posiciones actualizadas");
        const updated = await fetchAndGroupPermissions(recordOrderId);
        setParticipants(updated);
        onChange?.();
      }
    } catch (error) {
      console.error("Error actualizando posiciones:", error);
      message.error("Error al actualizar posiciones");
    } finally {
      setIsLoadingChange(false);
    }
  };

  const categorizedParticipants = useMemo(() => {
    const categories: Record<string, UserPermissions[]> = {
      customer: [],
      store: [],
      seller: [],
    };

    participants.forEach((p) => {
      if (categories[p.participationType]) {
        categories[p.participationType].push(p);
      }
    });

    return categories;
  }, [participants]);

  const categoryConfig = [
    { type: "customer", label: t("contacts.role.customer"), icon: <ContactsOutlined /> },
    { type: "store", label: "Store", icon: <ShopOutlined /> },
    { type: "seller", label: "Seller", icon: <SellerIcon /> },
  ];

  const handleSaveChanges = async (changes: Record<string, string>) => {
    if (Object.keys(changes).length === 0) return;

    setIsLoadingChange(true);
    try {
      await Promise.all(
        Object.entries(changes).map(async ([key, value]) => {
          const [groupKey, permissionCode] = key.split("||");
          const participant = participants.find(
            (p) =>
              buildGroupKey(
                p.contactId,
                p.participationType,
                p.participationIdentifier,
              ) === groupKey,
          );

          if (!participant) return;

          const payload = {
            contactId: participant.contactId,
            permissionCode,
            entityId: Number(recordOrderId),
            // Backend EntityType enum maps Customer to ''.
            participationType:
              participant.participationType === "customer"
                ? ""
                : participant.participationType,
            participationIdentifier: participant.participationIdentifier,
            value,
            position: participant.permissions[permissionCode]?.position ?? 0,
          };
          await axiosInstance.post(API_URL_PERMISSIONS_PARTICIPATION, payload);
        }),
      );

      message.success("Permisos actualizados");
      const updated = await fetchAndGroupPermissions(recordOrderId);
      setParticipants(updated);
      setDraftChanges({});
      onChange?.();
    } catch (error) {
      console.error("Error guardando permisos:", error);
      message.error("Error al guardar cambios");
    } finally {
      setIsLoadingChange(false);
    }
  };

  const handleAddParticipant = async (
    contact: any,
    permissions: { [code: string]: string },
  ) => {
    try {
      // El operador ELIGE los permisos en el modal: manda lo que se seleccionó.
      //
      // Antes esto se pisaba con la config del panel del operador
      // (`fetchInheritedOrderPerms`, ahora eliminada) y los valores del formulario se
      // ignoraban. Se revirtió a pedido: además de que el formulario quedaba decorativo,
      // esa config puede tener `by_relation` en el código `order` —un valor de
      // CONFIGURACIÓN, no de participación— y postearlo daba un 422.
      const permissionList = Object.entries(permissions)
        .filter(([, value]) => Boolean(value))
        .map(([code, value]) => ({ code, value }));

      if (!permissionList.length) {
        message.warning("Seleccioná al menos un permiso");
        return;
      }

      // Find highest current position to append
      const maxPos = participants.reduce(
        (max, p) => Math.max(max, p.permissions.order?.position ?? 0),
        -1,
      );
      const newPos = maxPos + 1;

      const basePayload = {
        contactId: contact.contactId,
        entityId: Number(recordOrderId),
        participationType: contact?.role === "customer" ? "" : contact?.role,
        // Mismo criterio que en los participantes del Presupuesto: el identifier es el código del
        // cargo elegido (`roleCode`), no el store del pedido. Ver el comentario largo en
        // `pages/quotes/detail/QuotePermissionsLateral.tsx`.
        participationIdentifier:
          contact?.role === "customer" ? store?.code : contact?.roleCode || store?.code,
        position: newPos,
      };

      await Promise.all(
        permissionList.map(({ code, value }) =>
          axiosInstance.post(API_URL_PERMISSIONS_PARTICIPATION, {
            ...basePayload,
            permissionCode: code,
            value,
          }),
        ),
      );

      const updated = await fetchAndGroupPermissions(recordOrderId);
      setParticipants(updated);
      message.success("Participante agregado");
      onChange?.();
    } catch (error) {
      console.error("Error al agregar participante:", error);
      message.error("Error al agregar participante");
    }
  };

  return (
    <LateralModal
      title={t("permissions")}
      buttonSize="small"
      buttonType="text"
      buttonName={t("permissions")}
      iconPosition="end"
      iconSvg={<LockOutlined />}
    >
      <Flex vertical style={{ flex: 1, overflow: "hidden" }}>
        <NormalModal
          footer={null}
          destroyOnClose
          open={isModalOpen}
          onCancel={handleCancel}
          title={t("contacts.label.add.relation")}
          icon={<UsergroupAddOutlined />}
          styles={{ content: { minHeight: 520 } }}
        >
          <PermissionsSelect
            handleCancel={handleCancel}
            onSaveContact={handleAddParticipant}
            store={store}
            contactStores={contactRecord}
          />
        </NormalModal>

        {/* Header fijo */}
        <Flex
          justify="space-between"
          align="center"
          style={{ padding: "16px 24px", flexShrink: 0 }}
        >
          <Flex gap={8} align="center">
            <ShopOutlined />
            <Text strong>{store?.name}</Text>
          </Flex>
          <Button
            size="small"
            shape="round"
            icon={<UsergroupAddOutlined />}
            onClick={showModal}
          >
            {t("buttom.add")}
          </Button>
        </Flex>

        {/* Contenido scrolleable */}
        <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
          {loading && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.6)", zIndex: 10 }}>
              <Spin />
            </div>
          )}
          <div style={{ height: "100%", overflowY: "auto", padding: "0 24px 24px" }}>
            {participants.length === 0 && !loading && (
              <Text
                type="secondary"
                italic
                style={{ textAlign: "center", marginTop: 20, display: "block" }}
              >
                {t("message.noParticipantsFound")}
              </Text>
            )}

            <Flex vertical gap={4}>
              {categoryConfig.map(({ type, label, icon }) => {
                const list = categorizedParticipants[type];
                if (list.length === 0) return null;

                return (
                  <StoreList
                    key={type}
                    type={type}
                    icon={type === "seller"}
                    title={
                      <Flex align="center" gap={8}>
                        {icon}
                        <Text style={{ textTransform: "capitalize" }}>
                          {type === "seller" && list[0]?.participationIdentifier
                            ? `${label}: ${list[0].participationIdentifier}`
                            : label}
                        </Text>
                      </Flex>
                    }
                    participants={list.map((p) => ({
                      id: buildGroupKey(
                        p.contactId,
                        p.participationType,
                        p.participationIdentifier,
                      ),
                      name: `${p.firstname} ${p.lastname}`,
                      position: p.permissions.order?.position ?? 0,
                      raw: { participant: p },
                    }))}
                    hideSelect
                    getParticipants={() =>
                      fetchAndGroupPermissions(recordOrderId)
                    }
                    onChange={(newList) => handleUpdatePosition(newList, type)}
                    renderHeader={(item) => {
                      const p = item.raw.participant;
                      const conf = PERMISSION_CONFIG[0];
                      const isOwner = item.position === 0;
                      return (
                        <PermissionSelectRow
                          key={conf.code}
                          badge={conf.badge}
                          strong={conf.strong}
                          label={
                            <Flex align="center" gap={6}>
                              <Text strong>{`${p.firstname} ${p.lastname}`}</Text>
                              {isOwner && (
                                <Text
                                  type="secondary"
                                  style={{ fontSize: "12px", fontWeight: "normal" }}
                                >
                                  (Dueño)
                                </Text>
                              )}
                            </Flex>
                          }
                          value={p.permissions[conf.code]?.value}
                          customerMode={type === "customer"}
                          onChange={(val) =>
                            handlePermissionChange(p, conf.code, val)
                          }
                          isLoadingChange={isLoadingChange}
                        />
                      );
                    }}
                    renderExtra={(item) => {
                      const p = item.raw.participant;
                      if (p.participationType === "customer") return null;
                      return (
                        <Flex vertical>
                          {PERMISSION_CONFIG.slice(1).map((conf) => (
                            <PermissionSelectRow
                              key={conf.code}
                              badge={conf.badge}
                              strong={conf.strong}
                              label={conf.label}
                              value={p.permissions[conf.code]?.value}
                              onChange={(val) =>
                                handlePermissionChange(p, conf.code, val)
                              }
                              isLoadingChange={isLoadingChange}
                              asSwitch
                            />
                          ))}
                        </Flex>
                      );
                    }}
                  />
                );
              })}
            </Flex>
          </div>
        </div>
      </Flex>
    </LateralModal>
  );
};
